import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  adminLogin,
  adminMe,
  clearAdminSession,
  getAdminProfile,
  getAdminToken,
  type AdminProfile,
} from '../services/adminApi'

type AdminAuthValue = {
  admin: AdminProfile | null
  loading: boolean
  error: string | null
  login: (username: string, password: string) => Promise<AdminProfile | null>
  logout: () => void
  isAuthenticated: boolean
}

const AdminAuthContext = createContext<AdminAuthValue | null>(null)

function isUnauthorized(err: unknown) {
  if (!(err instanceof Error)) return false
  const msg = err.message.toLowerCase()
  return msg.includes('session expired') || msg.includes('401') || msg.includes('unauthorized') || msg.includes('invalid or missing')
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminProfile | null>(() => getAdminProfile())
  const [loading, setLoading] = useState(Boolean(getAdminToken()))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!getAdminToken()) {
      setLoading(false)
      return
    }
    adminMe()
      .then((json) => setAdmin(json.admin))
      .catch((err) => {
        if (isUnauthorized(err)) {
          clearAdminSession()
          setAdmin(null)
        }
      })
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    setError(null)
    setLoading(true)
    try {
      const profile = await adminLogin(username, password)
      setAdmin(profile)
      return profile
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Login failed'
      setError(msg)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const logout = useCallback(() => {
    clearAdminSession()
    setAdmin(null)
    setError(null)
  }, [])

  const value = useMemo(
    () => ({
      admin,
      loading,
      error,
      login,
      logout,
      isAuthenticated: Boolean(admin),
    }),
    [admin, loading, error, login, logout],
  )

  return createElement(AdminAuthContext.Provider, { value }, children)
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext)
  if (!ctx) {
    throw new Error('useAdminAuth must be used inside AdminAuthProvider')
  }
  return ctx
}
